import { i18n } from 'i18next'
import Link from 'next/link'
import Image from 'next/image'
import React, { useState, useRef, useEffect, memo, useCallback } from 'react'
import { MenuOutlined, CloseOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { useRouter } from 'next/navigation'
import logoPng from '@/public/logo.png'
import { useUserStore } from '@/stores/user-store-provider'
import { LogoutAction, UpdateCurrencyAction, MyInfoAction } from '@/actions/user'
import { HomePageAction, CartListAction } from '@/actions/shop'
import ErrorMessage from '@/components/ErrorMessage'
import { currencyArr, langArr } from '@/utils/settings'
import SearchSvg from '@/public/icons/search.svg'
import UserSvg from '@/public/icons/user.svg'
import ShoppingCartSvg from '@/public/icons/shoppingCart.svg'

export const HeaderBase = memo(function HeaderBase({ i18n, lng, path = '', toggleSidebar, isSidebarOpen }: { i18n: i18n; lng: string; path?: string; toggleSidebar?: () => void; isSidebarOpen?: boolean }) {
  const { isLogin, name, currency, currencySign, cartNum, exchangeRate, updateIsLogin, updateCurrency, updateCurrencySign, updateExchangeRate, updateCartNum, updateBookIdList } = useUserStore((state) => state)
  const t = i18n.getFixedT(lng, 'header')
  const router = useRouter()
  const [shopTopCategories, setShopTopCategories] = useState([])
  const [uiState, setUiState] = useState({
    allBooksDropdownVisible: false,
    currencyDropdownVisible: false,
    languageDropdownVisible: false,
    searchVisible: false,
    isLoading: false,
    categoriesLoading: true,
    isScrolled: false
  })

  // 获取书籍分类
  const getBookCategory = useCallback(async () => {
    try {
      setUiState((prev) => ({ ...prev, categoriesLoading: true }))
      const { result: homeData, success, errorLangs } = await HomePageAction()
      if (success) {
        const { shopTopCategories } = homeData
        setShopTopCategories(shopTopCategories)
      } else {
        console.error('Failed to load categories:', errorLangs[lng])
      }
    } catch (error) {
      console.error('Error loading categories:', error)
    } finally {
      setUiState((prev) => ({ ...prev, categoriesLoading: false }))
    }
  }, [lng])

  useEffect(() => {
    getBookCategory()
  }, [getBookCategory])

  // 监听滚动事件，添加阴影效果
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 0
      setUiState((prev) => ({ ...prev, isScrolled: scrolled }))
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const getCookie = (name: string) => {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) {
        const cookieValue = parts.pop()?.split(';').shift()
        return cookieValue ? decodeURIComponent(cookieValue) : null
      }
      return null
    }

    const cookieCurrency = getCookie('currency') || 'CNY'
    const cookieCurrencySign = getCookie('currencySign') || '¥'

    if (isLogin) {
      const initCurrencyCartNumBookIdList = async () => {
        try {
          setUiState((prev) => ({ ...prev, isLoading: true }))

          const [currencyActionData, cartListData, myInfoData] = await Promise.all([UpdateCurrencyAction({ currency: cookieCurrency, currencySign: cookieCurrencySign }), CartListAction({}), MyInfoAction()])
          // 更新初始汇率
          if (currencyActionData.success) {
            const _exchangeRate = Math.round((currencyActionData.result.remark / currencyActionData.result.remark2) * 1000000) / 1000000
            updateExchangeRate(_exchangeRate)
          } else {
            console.error('Currency update failed:', currencyActionData.errorLangs[lng])
          }
          // 更新用户购物车数量
          if (cartListData.success) {
            updateCartNum(cartListData.result.notBuyList.length)
          } else {
            console.error('Cart data failed:', cartListData.errorLangs[lng])
          }
          // 更新用户已购书籍ID列表
          if (myInfoData.success) {
            updateBookIdList(myInfoData.result.bookIdList)
          } else {
            console.error('User info failed:', myInfoData.errorLangs[lng])
          }
        } catch (error) {
          console.error('Error initializing user data:', error)
        } finally {
          setUiState((prev) => ({ ...prev, isLoading: false }))
        }
      }
      initCurrencyCartNumBookIdList()
    } else {
      // 用户未登录时也需要更新初始汇率
      const initCurrencyOnly = async () => {
        try {
          const currencyActionData = await UpdateCurrencyAction({ currency: cookieCurrency, currencySign: cookieCurrencySign })
          if (currencyActionData.success) {
            const _exchangeRate = Math.round((currencyActionData.result.remark / currencyActionData.result.remark2) * 1000000) / 1000000
            updateExchangeRate(_exchangeRate)
          } else {
            console.error('Currency update failed:', currencyActionData.errorLangs[lng])
          }
        } catch (error) {
          console.error('Error initializing currency:', error)
        }
      }
      initCurrencyOnly()
    }
  }, [isLogin])

  // 切换语言
  const changeLanguage = (key: string) => {
    setUiState((prev) => ({ ...prev, languageDropdownVisible: false }))
    const newPath = path.replace(/^\/(zh-CN|zh-HK|en)(\/|$)/, `/${key}$2`)
    router.push(newPath, { scroll: false })
  }
  // 修改货币
  const changeCurrency = async (currencyItem: any) => {
    try {
      setUiState((prev) => ({ ...prev, currencyDropdownVisible: false }))
      const resActionData: any = await UpdateCurrencyAction({ currency: currencyItem.label.split('(')[0], currencySign: currencyItem.key })
      if (resActionData.success) {
        updateCurrency(resActionData.result.currency)
        updateCurrencySign(currencyItem.key)
        const exchangeRate = Math.round((resActionData.result.remark / resActionData.result.remark2) * 1000000) / 1000000
        updateExchangeRate(exchangeRate)
      } else {
        console.error('Currency change failed:', resActionData.errorLangs[lng])
      }
    } catch (error) {
      console.error('Error changing currency:', error)
    }
  }

  // 退出登录
  const logout = async () => {
    try {
      const resActionData: any = await LogoutAction()
      if (resActionData.success) {
        localStorage.removeItem('userStore')
        updateIsLogin(false)
        window.location.href = `/${lng}/login`
      } else {
        console.error('Logout failed:', resActionData.errorLangs?.[lng])
      }
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  // 显示搜索表单
  const inputRef = useRef<HTMLInputElement>(null)
  const showSearchForm = useCallback(() => {
    setUiState((prev) => ({ ...prev, searchVisible: true }))
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }, [])

  // 隐藏搜索表单
  const hideSearchForm = useCallback(() => {
    setUiState((prev) => ({ ...prev, searchVisible: false }))
    inputRef.current?.blur()
  }, [])

  // 搜索书籍
  const searchAction = useCallback(() => {
    if (inputRef.current?.value) {
      router.push(`/${lng}/all-books/searchText/${inputRef.current.value}`)
    }
    if (inputRef.current) inputRef.current.value = ''
    hideSearchForm()
  }, [])

  // 清空搜索框
  const clearSearch = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.focus()
    }
  }

  return (
    <header
      className={`fixed left-0 top-0 z-[9999] min-h-[81px] w-full border-b border-[#f4f4f4] bg-white transition-[margin-left] duration-[400ms] ease-in-out
        ${isSidebarOpen ? 'ml-[290px]' : 'ml-0'} ${uiState.isScrolled ? 'shadow-[0_4px_10px_0_rgba(47,56,68,0.10)]' : ''}`}>
      <form className={`${uiState.searchVisible ? 'opacity-1 visible' : 'invisible opacity-0'} absolute z-10 block h-full w-full bg-white`} action={searchAction}>
        {/* 搜索：书名/作者/出版社 */}
        <input ref={inputRef} type="text" placeholder={t('searchPlaceholder')} className="absolute m-0 block h-full w-full border-0 bg-white pl-[30px] pr-[120px] text-[16px] text-black focus:outline-none" autoComplete={'off'} />
        <input type="hidden" name="order" value="3" />
        <div className="absolute right-[15px] top-[50%] z-[5] -mt-5 lg:right-[30px]">
          <span className="inline-block cursor-pointer px-0 py-2.5 align-middle text-[11px] font-medium uppercase tracking-[.1em] text-gray transition-colors duration-[250ms]" onClick={clearSearch}>
            Clear
          </span>
          <span className="ml-2.5 inline-block cursor-pointer pb-2.5 pl-2.5 pt-1 align-middle transition-colors duration-[250ms]">
            <CloseOutlined className="relative top-[3px] h-[14px] w-[14px] text-black" onClick={hideSearchForm} />
          </span>
        </div>
      </form>
      <div className="absolute left-0 top-0 z-[5] h-full">
        <div className="table h-full min-h-full">
          <span onClick={toggleSidebar} className="cats-toggle table-cell w-[56px] cursor-pointer text-center align-middle text-[20px] leading-[1] text-black transition-colors duration-[300ms] xl:w-[74px]">
            <Button type="text" icon={<MenuOutlined />} className="inline-block hover:!bg-transparent hover:!text-red" />
          </span>
          <Link className="table-cell w-[125px] bg-transparent py-3 pl-4 pr-0 align-middle no-underline xl:w-[154px] xl:pl-[25px]" href="/">
            <Image src={logoPng} alt="logo" width={0} height={0} className="block h-auto w-full max-w-full border-none align-middle" />
          </Link>
        </div>
      </div>
      <nav className="site-menu">
        <ul className="!hidden lg:!table">
          <li className="group">
            <div className="menu-switcher-wrap" onMouseEnter={() => setUiState((prev) => ({ ...prev, allBooksDropdownVisible: true }))} onMouseLeave={() => setUiState((prev) => ({ ...prev, allBooksDropdownVisible: false }))}>
              <div className="menu-icon">
                <Link href={`/${lng}/all-books`}>
                  <span className="inline-block pb-1 pr-0 pt-1.25 align-middle text-[14px] uppercase transition-colors duration-300 after:absolute after:left-0 after:top-full after:inline-block after:h-[30px] after:w-full after:content-['']">{t('allBooks')}</span>
                </Link>
              </div>
              <ul className={`${uiState.allBooksDropdownVisible ? '!visible' : '!invisible'} dropdown-menu ${lng === 'en' ? 'left-[-180px]' : 'left-[-124px]'} py-2.5 leading-[1.5]`}>
                <div className="flex justify-between whitespace-nowrap" onClick={() => setUiState((prev) => ({ ...prev, allBooksDropdownVisible: false }))}>
                  <div className="mb-4">
                    {/* 书籍分类 */}
                    <li className={`bookGrouped ${lng === 'en' ? 'pl-6 pr-0' : 'px-9'} py-3.75 text-left text-[15px] font-bold`}>{t('bookCategories')}</li>
                    {shopTopCategories.map((item: any) => (
                      <li key={item.categoryId} className="subBookItem">
                        <Link href={`/${lng}/all-books/category/${item.categoryId}`} className={`${lng === 'en' ? '!pl-6 !pr-0' : ''}`}>
                          {lng === 'en' ? item.categoryNameEn : lng === 'zh-CN' ? item.categoryNameSc : item.categoryNameTc}
                        </Link>
                      </li>
                    ))}
                  </div>
                  <div>
                    {/* 书籍语言 */}
                    <li className={`bookLanguage ${lng === 'en' ? 'pl-4 pr-6' : 'px-9'} py-3.75 text-[15px] font-bold`}>{t('booksByLanguage')}</li>
                    <li className="subBookItem">
                      <Link href={`/${lng}/all-books/bookLanguage/simplifiedChinese`} className={`${lng === 'en' ? '!pl-4 !pr-0' : ''}`}>
                        <span>{t('simplifiedChineseBooks')}</span>
                      </Link>
                    </li>
                    <li className="subBookItem">
                      <Link href={`/${lng}/all-books/bookLanguage/traditionalChinese`} className={`${lng === 'en' ? '!pl-4 !pr-0' : ''}`}>
                        <span>{t('traditionalChineseBooks')}</span>
                      </Link>
                    </li>
                    <li className="subBookItem">
                      <Link href={`/${lng}/all-books/bookLanguage/english`} className={`${lng === 'en' ? '!pl-4 !pr-0' : ''}`}>
                        <span>{t('englishBooks')}</span>
                      </Link>
                    </li>
                  </div>
                </div>
              </ul>
            </div>
          </li>
          <li>
            <Link href={`/${lng}/inspirata-publications`}>
              <span>{t('inspirata')}</span>
            </Link>
          </li>
          <li>
            <Link href={`/${lng}/free-books`}>
              <span>{t('free')}</span>
            </Link>
          </li>
          <li>
            <Link href={`/${lng}/reading-partner`}>
              <span>{t('readingPartner')} </span>
            </Link>
          </li>
          <li>
            <Link href={`/${lng}/rent-to-own`}>
              <span>{t('rentToOwn')}</span>
            </Link>
            {/* <Image src="https://dl.endao.co/fire.png" alt="new" width={0} height={0} className="absolute right-1.5 top-5 h-auto w-[16px] max-w-full align-middle" /> */}
          </li>
          <li>
            <Link href={`/${lng}/blogs`}>
              <span>{t('blog')}</span>
            </Link>
          </li>
          <li>
            <Link href={`/${lng}/app-download`} className="!pr-[7px]">
              <span>{t('download')}</span>
            </Link>
          </li>
          <li className="group">
            <div className="menu-switcher-wrap" onMouseEnter={() => setUiState((prev) => ({ ...prev, currencyDropdownVisible: true }))} onMouseLeave={() => setUiState((prev) => ({ ...prev, currencyDropdownVisible: false }))}>
              <div className="menu-icon">
                <span className="ml-1 mr-[1px] inline-block border-l border-[#e1e7ec] py-1 pl-2.5 pr-0 align-middle text-[14px] uppercase transition-colors duration-300 after:absolute after:left-[5px] after:top-full after:inline-block after:h-[30px] after:w-full after:content-['']">{`${currency}(${currencySign})`}</span>
              </div>
              <ul className={`dropdown-menu -left-1 right-0 ${uiState.currencyDropdownVisible ? '!visible' : '!invisible'} pb-3`}>
                {currencyArr
                  .filter((item: any) => item.key !== currencySign)
                  .map((item: any) => (
                    <li key={item.key}>
                      <Link
                        className="dropdown-item"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          changeCurrency(item)
                        }}>
                        {item.label}
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          </li>
          <li className="group">
            <div className="menu-switcher-wrap" onMouseEnter={() => setUiState((prev) => ({ ...prev, languageDropdownVisible: true }))} onMouseLeave={() => setUiState((prev) => ({ ...prev, languageDropdownVisible: false }))}>
              <div className="menu-icon">
                <span className="ml-2.5 inline-block border-l border-solid border-[#e1e7ec] py-1 pl-2.5 pr-[2px] align-middle text-[14px] transition-colors duration-300">{lng === 'zh-CN' ? '简体' : lng === 'zh-HK' ? '繁體' : 'English'}</span>
              </div>
              <ul className={`dropdown-menu ${lng === 'en' ? '-left-4' : '-left-6'} pb-3 ${uiState.languageDropdownVisible ? '!visible' : '!invisible'}`}>
                {langArr.map((item: any) => (
                  <li key={item.key}>
                    <a
                      className="dropdown-item !text-center"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        changeLanguage(item.key)
                      }}>
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </li>
        </ul>
      </nav>

      <div className="absolute right-[15px] top-0 z-[5] flex h-full items-center justify-center xl:right-[30px]">
        {/* 搜索图标 */}
        <Button onClick={showSearchForm} size="small" shape="circle" icon={<SearchSvg className="relative left-[1px] top-[1px]" />} className="mr-1.25 !h-[44px] !w-[44px] hover:!bg-[#f7f7f7] xl:mr-2.5" />
        {/* 用户图标 */}
        <div className="group invisible after:absolute lg:visible">
          <Link href={`/${lng}/${isLogin ? 'my-center' : 'login'}`}>
            <Button shape="circle" size="small" icon={<UserSvg className="relative left-0 top-[1px]" />} className="!h-[44px] !w-[44px] after:absolute after:top-full after:-mt-2.5 after:block after:h-[34px] after:w-full after:content-[''] hover:!bg-[#f7f7f7]" />
          </Link>
          <ul
            className={`user-dropdown absolute left-[80px] top-[74px] ${isLogin ? 'ml-[-100px]' : 'ml-[-74px]'} invisible -mt-4 ${isLogin ? 'w-[192px]' : 'w-[134px]'} rounded-[4px] border border-transparent bg-white px-0 py-2.5 leading-[1.5] opacity-0 shadow-[0_7px_22px_-5px_rgba(47,56,68,0.2)] transition-all duration-300 ease-in-out before:!top-[-7px] after:!top-[-6px] group-hover:visible group-hover:!mt-[7px] group-hover:opacity-100`}>
            {!isLogin ? (
              <>
                {/* 注册 */}
                <li>
                  <Link href={`/${lng}/register`} className="block px-9 py-1.25 !text-left text-[14px] text-black">
                    {t('register')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${lng}/login`} className="block px-9 py-1.25 !text-left text-[14px] text-black">
                    {t('login')}
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li>
                  <h6 className="mb-[2px] px-[17px] pb-1.5  pt-1 !text-left text-[14px] leading-[1.4]">{name}</h6>
                </li>
                <li>
                  <Link href={`/${lng}/my-center`} className="block px-9 py-1.25 !text-left text-[14px] text-black">
                    {t('myCenter')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${lng}/rent-to-own/list`} className="block px-9 py-1.25 !text-left text-[14px] text-black">
                    {t('myRent')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${lng}/my-devices`} className="block px-9 py-1.25 !text-left text-[14px] text-black">
                    {t('myDevices')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${lng}/my-orders`} className="block px-9 py-1.25 !text-left text-[14px] text-black">
                    {t('myOrders')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${lng}/my-wishes`} className="block px-9 py-1.25 !text-left text-[14px] text-black">
                    {t('myWishes')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${lng}/my-gift-history`} className="block px-9 py-1.25 !text-left text-[14px] text-black">
                    {t('myGiftHistory')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${lng}/my-redeem-code`} className="block px-9 py-1.25 !text-left text-[14px] text-black">
                    {t('myRedeemCode')}
                  </Link>
                </li>
                <li className="mt-1.25 border-t border-grayE1 pt-[3px]" onClick={logout}>
                  <span className="block cursor-pointer px-9 py-1.25 !text-left text-[14px] text-black hover:text-red">{t('signOut')}</span>
                </li>
              </>
            )}
          </ul>
        </div>
        {/* 购物车图标 */}
        {isLogin && (
          <Link href={`/${lng}/my-cart`} className="group ml-1.25 flex h-[44px] items-center rounded-[22px] border border-grayE1 px-4 hover:bg-[#f7f7f7] xl:ml-2.5">
            <ShoppingCartSvg className="mr-1.5 group-hover:!text-black" />
            <span className="text-[14px] group-hover:!text-black">{cartNum}</span>
          </Link>
        )}
      </div>
    </header>
  )
})
